/**
 * Email domain validation utility for sales portal
 * Only allows emails from @anchorallied.com domain
 */

/**
 * Allowed email domains for the sales portal
 */
const ALLOWED_DOMAINS = ['anchorallied.com'];

/**
 * Check if a string looks like an email address
 * @param input - String to check
 * @returns true if input contains @ symbol
 */
export function isEmailFormat(input: string): boolean {
  if (!input || typeof input !== 'string') return false;
  return input.includes('@') && input.trim().length > 0;
}

/**
 * Check if email appears complete enough to validate
 * Only validates when email has @ and domain with at least a dot
 * @param input - String to check
 * @returns true if email looks complete
 */
export function isEmailComplete(input: string): boolean {
  if (!input || typeof input !== 'string') return false;
  const trimmed = input.trim();
  // Must have @ and at least one character after @, and a dot in the domain part
  const parts = trimmed.split('@');
  if (parts.length !== 2) return false;
  const [local, domain] = parts;
  return local.length > 0 && domain.length > 0 && domain.includes('.');
}

/**
 * Validate email domain - must be from @anchorallied.com
 * @param email - Email address to validate
 * @returns Object with valid boolean and optional error message
 */
export function validateEmailDomain(email: string): { valid: boolean; error?: string } {
  if (!email || typeof email !== 'string') {
    return { valid: false, error: 'Email is required' };
  }

  const trimmedEmail = email.trim();
  
  // If empty after trim, consider it valid (email is optional in some forms)
  if (trimmedEmail === '') {
    return { valid: true };
  }

  // Basic email format check
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmedEmail)) {
    return { valid: false, error: 'Invalid email format' };
  }

  // Extract domain (case-insensitive)
  const domain = trimmedEmail.split('@')[1]?.toLowerCase();
  
  if (!domain) {
    return { valid: false, error: 'Invalid email format' };
  }

  // Check if domain is allowed
  const isAllowed = ALLOWED_DOMAINS.some(allowedDomain => 
    domain === allowedDomain.toLowerCase()
  );

  if (!isAllowed) {
    return { 
      valid: false, 
      error: 'Email must be from @anchorallied.com domain' 
    };
  }

  return { valid: true };
}

